import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Slider,
  IconButton,
  Chip,
  useTheme
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Fullscreen
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { useApiQuery } from '../../hooks/useApiCache';

// --- Utility functions ---

const getWeekNumber = (date) => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

const parseLocalDate = (str) => {
  const parts = String(str).split('-');
  if (parts.length >= 3) {
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  return new Date(str);
};

const formatDateName = (dateStr) => {
  if (!dateStr) return '';
  return parseLocalDate(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const getWeekKey = (dateStr) => {
  const date = parseLocalDate(dateStr);
  return `Semana ${getWeekNumber(date)}/${date.getFullYear()}`;
};

const getMonthKey = (dateStr) => {
  return parseLocalDate(dateStr).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};

const getYearKey = (dateStr) => {
  return parseLocalDate(dateStr).getFullYear().toString();
};

const processDataByPeriod = (data, periodType) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const calcPercentual = (previsto, realizado) =>
    previsto > 0 ? Math.round(((realizado / previsto) * 100) * 100) / 100 : 0;

  switch (periodType) {
    case 'daily':
      return data.map(item => {
        const previsto = Number(item.total_previsto) || 0;
        const realizado = Number(item.total_aberto) || 0;
        return {
          name: formatDateName(item.data),
          previsto,
          realizado,
          percentual_realizado: calcPercentual(previsto, realizado)
        };
      });

    case 'weekly': {
      const groups = {};
      data.forEach(item => {
        const key = getWeekKey(item.data);
        if (!groups[key]) groups[key] = { previsto: 0, realizado: 0 };
        groups[key].previsto += Number(item.total_previsto) || 0;
        groups[key].realizado += Number(item.total_aberto) || 0;
      });
      return Object.entries(groups).map(([week, d]) => ({
        name: week,
        previsto: d.previsto,
        realizado: d.realizado,
        percentual_realizado: calcPercentual(d.previsto, d.realizado)
      }));
    }

    case 'monthly': {
      const groups = {};
      data.forEach(item => {
        const key = getMonthKey(item.data);
        if (!groups[key]) groups[key] = { previsto: 0, realizado: 0 };
        groups[key].previsto += Number(item.total_previsto) || 0;
        groups[key].realizado += Number(item.total_aberto) || 0;
      });
      return Object.entries(groups).map(([month, d]) => ({
        name: month,
        previsto: d.previsto,
        realizado: d.realizado,
        percentual_realizado: calcPercentual(d.previsto, d.realizado)
      }));
    }

    case 'yearly': {
      const groups = {};
      data.forEach(item => {
        const key = getYearKey(item.data);
        if (!groups[key]) groups[key] = { previsto: 0, realizado: 0 };
        groups[key].previsto += Number(item.total_previsto) || 0;
        groups[key].realizado += Number(item.total_aberto) || 0;
      });
      return Object.entries(groups).map(([year, d]) => ({
        name: year,
        previsto: d.previsto,
        realizado: d.realizado,
        percentual_realizado: calcPercentual(d.previsto, d.realizado)
      }));
    }

    case 'all': {
      const total = data.reduce((acc, item) => {
        acc.previsto += Number(item.total_previsto) || 0;
        acc.realizado += Number(item.total_aberto) || 0;
        return acc;
      }, { previsto: 0, realizado: 0 });
      return [{
        name: 'Total',
        previsto: total.previsto,
        realizado: total.realizado,
        percentual_realizado: calcPercentual(total.previsto, total.realizado)
      }];
    }

    default:
      return [];
  }
};

// --- Component ---

const SeriesChart = ({ open, onClose, startDate, endDate, contrato, centro, equipe }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const disabledBg = isDark ? 'rgba(255, 255, 255, 0.1)' : 'grey.300';
  const disabledColor = isDark ? 'rgba(255, 255, 255, 0.3)' : 'grey.500';
  const [period, setPeriod] = useState('daily');
  const [visibleRange, setVisibleRange] = useState([0, 10]);
  const [isFullView, setIsFullView] = useState(false);

  // Compute effective date range based on selected period
  const { effectiveStart, effectiveEnd } = useMemo(() => {
    const anchorDate = endDate || startDate || new Date();
    const anchor = new Date(anchorDate);
    let effStart, effEnd;

    switch (period) {
      case 'daily':
        effStart = startDate ? new Date(startDate) : new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
        effEnd = endDate ? new Date(endDate) : new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
        break;
      case 'weekly':
        effStart = new Date(anchor);
        effStart.setDate(effStart.getDate() - 34);
        effStart = new Date(effStart.getFullYear(), effStart.getMonth(), effStart.getDate());
        effEnd = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
        break;
      case 'monthly':
        effStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        effEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
        break;
      case 'yearly':
        effStart = new Date(anchor.getFullYear(), anchor.getMonth() - 11, 1);
        effEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
        break;
      case 'all':
      default:
        effStart = startDate ? new Date(startDate) : anchor;
        effEnd = endDate ? new Date(endDate) : anchor;
        break;
    }

    return { effectiveStart: effStart, effectiveEnd: effEnd };
  }, [period, startDate, endDate]);

  // Fetch data with React Query cache (5 min staleTime)
  const { data: rawData, isLoading: loading, error: queryError } = useApiQuery(
    '/percentual-turnos-diario',
    { startDate: effectiveStart, endDate: effectiveEnd, contrato, centro, equipe },
    {
      enabled: open && !!effectiveStart && !!effectiveEnd,
      staleTime: 5 * 60 * 1000
    }
  );

  const error = queryError?.message || null;

  // Process raw data by selected period
  const chartData = useMemo(() => {
    return processDataByPeriod(Array.isArray(rawData) ? rawData : [], period);
  }, [rawData, period]);

  // Reset visible range when period changes
  React.useEffect(() => {
    setVisibleRange([0, 10]);
    setIsFullView(false);
  }, [period]);

  const getPeriodLabel = (p) => {
    const labels = { daily: 'Diário', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual', all: 'Geral' };
    return labels[p] || p;
  };

  // Navigation controls
  const handleSliderChange = (event, newValue) => {
    setVisibleRange(newValue);
  };

  const handlePrevious = () => {
    const [start, end] = visibleRange;
    const rangeSize = end - start;
    const newStart = Math.max(0, start - rangeSize);
    const newEnd = Math.min(chartData.length, newStart + rangeSize);
    setVisibleRange([newStart, newEnd]);
  };

  const handleNext = () => {
    const [start, end] = visibleRange;
    const rangeSize = end - start;
    const newEnd = Math.min(chartData.length, end + rangeSize);
    const newStart = Math.max(0, newEnd - rangeSize);
    setVisibleRange([newStart, newEnd]);
  };

  const handleZoomIn = () => {
    const [start, end] = visibleRange;
    const rangeSize = end - start;
    const newRangeSize = Math.max(5, Math.floor(rangeSize * 0.7));
    const center = Math.floor((start + end) / 2);
    const newStart = Math.max(0, center - Math.floor(newRangeSize / 2));
    const newEnd = Math.min(chartData.length, newStart + newRangeSize);
    setVisibleRange([newStart, newEnd]);
  };

  const handleZoomOut = () => {
    const [start, end] = visibleRange;
    const rangeSize = end - start;
    const newRangeSize = Math.min(chartData.length, Math.floor(rangeSize * 1.3));
    const center = Math.floor((start + end) / 2);
    const newStart = Math.max(0, center - Math.floor(newRangeSize / 2));
    const newEnd = Math.min(chartData.length, newStart + newRangeSize);
    setVisibleRange([newStart, newEnd]);
  };

  const handleFullView = () => {
    setIsFullView(!isFullView);
    if (!isFullView) {
      setVisibleRange([0, chartData.length]);
    } else {
      setVisibleRange([0, 10]);
    }
  };

  const getVisibleData = () => {
    if (isFullView || chartData.length <= 10) return chartData;
    return chartData.slice(visibleRange[0], visibleRange[1]);
  };

  const getAveragePrevisto = () => {
    const data = getVisibleData();
    if (data.length === 0) return 0;
    const sum = data.reduce((acc, item) => acc + (item.previsto || 0), 0);
    return Math.round(sum / data.length * 100) / 100;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Séries - Percentual de Turnos Realizados
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Desempenho — {getPeriodLabel(period)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Linha laranja tracejada: Média dos valores previstos ({getAveragePrevisto()})
                </Typography>
                {period === 'daily' && chartData.length > 10 && !isFullView && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      label={`${visibleRange[0] + 1}-${visibleRange[1]} de ${chartData.length} dias`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Use os controles para navegar
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ToggleButtonGroup
                  value={period}
                  exclusive
                  onChange={(_, newValue) => newValue && setPeriod(newValue)}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="daily">Diário</ToggleButton>
                  <ToggleButton value="weekly">Semanal</ToggleButton>
                  <ToggleButton value="monthly">Mensal</ToggleButton>
                  <ToggleButton value="yearly">Anual</ToggleButton>
                  <ToggleButton value="all">Geral</ToggleButton>
                </ToggleButtonGroup>

                {/* Controles de navegação para visualização diária */}
                {period === 'daily' && chartData.length > 10 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <IconButton
                      size="small"
                      onClick={handlePrevious}
                      disabled={visibleRange[0] === 0}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: disabledBg, color: disabledColor }
                      }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={handleZoomOut}
                      disabled={visibleRange[1] - visibleRange[0] >= chartData.length}
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' },
                        '&:disabled': { bgcolor: disabledBg, color: disabledColor }
                      }}
                    >
                      <ZoomOut fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={handleZoomIn}
                      disabled={visibleRange[1] - visibleRange[0] <= 5}
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' },
                        '&:disabled': { bgcolor: disabledBg, color: disabledColor }
                      }}
                    >
                      <ZoomIn fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={handleNext}
                      disabled={visibleRange[1] === chartData.length}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: disabledBg, color: disabledColor }
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={handleFullView}
                      sx={{
                        bgcolor: isFullView ? 'success.main' : 'info.main',
                        color: 'white',
                        '&:hover': { bgcolor: isFullView ? 'success.dark' : 'info.dark' }
                      }}
                    >
                      <Fullscreen fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="error">{error}</Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
              </Box>
            ) : (
              <Box>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={getVisibleData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12 }}
                      angle={getVisibleData().length > 10 ? -45 : 0}
                      textAnchor={getVisibleData().length > 10 ? 'end' : 'middle'}
                      height={getVisibleData().length > 10 ? 80 : 60}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
                      domain={[0, 'dataMax + 5']}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const percentual = data?.percentual_realizado || 0;
                          const previsto = payload.find(p => p.dataKey === 'previsto')?.value || 0;
                          const realizado = payload.find(p => p.dataKey === 'realizado')?.value || 0;

                          return (
                            <Box
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                p: 1.5
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                Período: {label}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#2196f3' }}>
                                  Previsto: {previsto}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                                  Realizado: {realizado}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#ff9800',
                                    fontWeight: 600,
                                    mt: 0.5,
                                    pt: 0.5,
                                    borderTop: '1px solid #e0e0e0'
                                  }}
                                >
                                  % Realizado: {percentual.toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="previsto"
                      radius={[4, 4, 0, 0]}
                      fill="#2196f3"
                      name="previsto"
                    />
                    <Bar
                      dataKey="realizado"
                      radius={[4, 4, 0, 0]}
                      fill="#4caf50"
                      name="realizado"
                    />

                    {/* Linha da média dos valores previstos */}
                    <ReferenceLine
                      y={getAveragePrevisto()}
                      stroke="#ff9800"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      label={{
                        value: `Média Previsto: ${getAveragePrevisto()}`,
                        position: "topRight",
                        style: {
                          fill: '#ff9800',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>

                {period === 'daily' && chartData.length > 10 && !isFullView && (
                <Box sx={{ mt: 3, px: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}>
                    Navegação por período
                  </Typography>
                  <Slider
                    value={visibleRange}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={chartData.length}
                    step={1}
                    marks={[
                      { value: 0, label: 'Início' },
                      { value: chartData.length, label: 'Fim' }
                    ]}
                    sx={{
                      color: 'primary.main',
                      '& .MuiSlider-thumb': {
                        width: 20,
                        height: 20,
                        bgcolor: 'primary.main',
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)',
                        },
                      },
                      '& .MuiSlider-track': {
                        height: 6,
                        borderRadius: 3,
                      },
                      '& .MuiSlider-rail': {
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.300',
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'grey.400',
                        height: 8,
                        width: 8,
                        borderRadius: '50%',
                      },
                      '& .MuiSlider-markLabel': {
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                      },
                    }}
                  />
                </Box>
              )}
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeriesChart;
